/**
 * Supported locales for legacy frontend
 *
 * Currently only en-US is supported, but this can be expanded
 * to include additional locales as they are added to Lokalise.
 *
 */
type SupportedLocale =
  | 'en-US'
  | 'el-GR'
  | 'fr-CA'
  | 'nl-NL'
  | 'sv-SE'
  | 'zh-TW'
  | 'fr-FR'
  | 'de-DE'
  | 'es-ES'
  | 'it-IT'
  | 'ja-JP'
  | 'zh-CN';

/**
 * Structure of a single compiled message
 *
 * This matches the AST format output by @formatjs/cli compile
 */
interface LocaleMessage {
  type: number;
  value: string;
}

/**
 * Structure of compiled messages file
 *
 * Keys are SHA-512 hashed message IDs
 * Values are arrays of LocaleMessage AST nodes
 */
interface CompiledMessages {
  [key: string]: LocaleMessage[];
}

/**
 * Dynamically loads compiled locale data
 *
 * Uses dynamic imports to lazy-load locale bundles, reducing
 * initial bundle size. Falls back to en-US if the requested
 * locale fails to load.
 *
 * @param locale - The locale to load
 * @returns Promise resolving to the compiled messages
 *
 * @example
 * ```typescript
 * const messages = await loadLocaleData('en-US');
 * // Returns: { default: { "+DThNg": [{type: 0, value: "Save"}], ... } }
 * ```
 */
export const loadLocaleData = async (
  locale: SupportedLocale
): Promise<{ default: CompiledMessages }> => {
  try {
    // Dynamic import of compiled locale bundle
    // Rspack will code-split these into separate chunks
    const messages = await import(`../../locales-compiled/${locale}.json`);
    return messages;
  } catch (error) {
    console.warn(
      `[loadLocaleData] Failed to load locale ${locale}, falling back to en-US`,
      error
    );

    // Fallback to en-US if requested locale not found
    return import('../../locales-compiled/en-US.json');
  }
};
